import React, {useState} from "react";
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {flightService} from "@/services/flightService";
import {storage, STORAGE_KEYS} from "@/utils/storage";
import {plane2dIcon} from "@img/index.js";
import {ModalFooter} from "../Components/BookingModalShell.jsx";

export function FlightNumber(props) {
    const [flightNumber, setFlightNumber] = useState(storage.get(STORAGE_KEYS.FLIGHT_NUMBER, ''));
    const [flightNumberDetails, setFlightNumberDetails] = useState(storage.get(STORAGE_KEYS.FLIGHT_NUMBER_DETAILS, []));
    const [flightLoading, setFlightLoading] = useState(false);
    const [flightDetailsError, setFlightDetailsError] = useState(false);
    const [forcedFlightId, setForcedFlightId] = useState(false);

    const handleFlightNumberDetails = async (e) => {
        e.preventDefault();
        setFlightLoading(true);
        setFlightNumberDetails([]);
        setFlightDetailsError(false);
        setForcedFlightId(false);
        const result = await flightService.getFlightDetails(flightNumber);
        setFlightLoading(false);
        if (!result.success) {
            setFlightDetailsError(result.error || "Failed to fetch flight details");
            return;
        }
        if (result.data.length > 0) {
            const arrivalUtc = result.data[0].arrival?.scheduledTime?.utc;
            if (!arrivalUtc) {
                storage.set(STORAGE_KEYS.FLIGHT_NUMBER_DETAILS, result.data);
                setFlightNumberDetails(result.data);
                return;
            }
            const flightCheck = flightService.isFlightUpcoming(arrivalUtc);
            if (flightCheck.ok) {
                storage.set(STORAGE_KEYS.FLIGHT_NUMBER_DETAILS, result.data);
                setFlightNumberDetails(result.data);
            } else if (flightCheck.reason === 'too_soon') {
                setFlightDetailsError(`This flight arrives too soon. Online booking requires at least ${flightService.formatMinLeadTime()} advance notice.`);
            } else {
                setFlightDetailsError("Sorry, this flight has already arrived.");
            }
        }
    };

    return (
        <>
            <ModalHeader
                title="Flight Details"
                extraClasses=""
                totalSteps={0}
                cancelButton={true}
                cancelButtonType="icon"
                showBackButton={false}
                setStep={props.setStep}
                closeModal={props.closeModal}
            />

            <div className="hopn-flight-manual modalPages-spacing">
                <h2 className="hopn-flight-title">Enter Flight Number</h2>

                <form className="hopn-flight-input-wrap" onSubmit={handleFlightNumberDetails}>
                    <input
                        type="text"
                        name="flight_number"
                        value={flightNumber}
                        placeholder="E.g. Ac4567"
                        required
                        onChange={(e) => {
                            setFlightNumber(e.target.value);
                            storage.set(STORAGE_KEYS.FLIGHT_NUMBER, e.target.value);
                        }}
                    />
                    <button type="submit">Add</button>
                </form>

                {flightLoading && (<div className="loading"><Loader/></div>)}

                {flightNumberDetails.length > 0 && (
                    <div className="hopn-airlines-cards hopn-airlines">
                        <div className="hopn-airlines-card">
                            <div className="hopn-airlines-card-top">
                                {flightNumberDetails[0].airline?.name || flightNumberDetails[0].number}
                                {flightNumberDetails[0].aircraft?.model && <span>– {flightNumberDetails[0].aircraft.model}</span>}
                            </div>
                            <div className="hopn-airlines-card-body">
                                <div className="hopn-airlines-from">
                                    <span className="hopn-airlines-city">
                                        {flightNumberDetails[0].departure?.airport?.municipalityName || flightNumberDetails[0].departure?.airport?.name || '—'} {flightNumberDetails[0].departure?.airport?.iata ? `(${flightNumberDetails[0].departure.airport.iata})` : ''}
                                    </span>
                                    <span className="hopn-airlines-time">
                                        {flightNumberDetails[0].departure?.scheduledTime?.local ? flightService.formatFlightTime(flightNumberDetails[0].departure.scheduledTime.local) : '—'}
                                    </span>
                                    <span className="hopn-airlines-date">
                                        {flightNumberDetails[0].departure?.scheduledTime?.local ? flightService.formatFlightDate(flightNumberDetails[0].departure.scheduledTime.local) : ''}
                                    </span>
                                </div>

                                <div className="hopn-airlines-icon"><img decoding="async" src={plane2dIcon}/></div>

                                <div className="hopn-airlines-to">
                                    <span className="hopn-airlines-city">
                                        {flightNumberDetails[0].arrival?.airport?.municipalityName || flightNumberDetails[0].arrival?.airport?.name || '—'} {flightNumberDetails[0].arrival?.airport?.iata ? `(${flightNumberDetails[0].arrival.airport.iata})` : ''}
                                    </span>
                                    <span className="hopn-airlines-time">
                                        {flightNumberDetails[0].arrival?.scheduledTime?.local ? flightService.formatFlightTime(flightNumberDetails[0].arrival.scheduledTime.local) : '—'}
                                    </span>
                                    <span className="hopn-airlines-date">
                                        {flightNumberDetails[0].arrival?.scheduledTime?.local ? flightService.formatFlightDate(flightNumberDetails[0].arrival.scheduledTime.local) : ''}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {flightDetailsError && (
                    <div className="error">
                        {flightDetailsError}
                        {!flightDetailsError.includes('already arrived') && !flightDetailsError.includes('too soon') && flightNumber && (
                            <button
                                type="button"
                                className="hopn-force-flight-btn"
                                onClick={() => {
                                    setForcedFlightId(true);
                                    setFlightDetailsError(false);
                                }}
                            >
                                Continue with this ID
                            </button>
                        )}
                    </div>
                )}

                {forcedFlightId && !flightDetailsError && flightNumberDetails.length === 0 && (
                    <div className="hopn-forced-flight-info">
                        Continuing with flight: <strong>{flightNumber}</strong>
                    </div>
                )}

            </div>
            <ModalFooter>
                <button
                    className="hopn-add-passenger-button"
                    onClick={() => {
                        if (flightNumberDetails.length > 0) {
                            const flightDateStr = flightNumberDetails[0].departure.scheduledTime.local;
                            const flightDateObj = flightService.parseLocalTime(flightDateStr);
                            const mins = flightDateObj.getMinutes();
                            const remainder = mins % 5;
                            if (remainder !== 0) {
                                flightDateObj.setMinutes(mins + (5 - remainder), 0, 0);
                            } else {
                                flightDateObj.setSeconds(0, 0);
                            }
                            const flightDateOnly = new Date(flightDateObj);
                            flightDateOnly.setHours(0, 0, 0, 0);
                            storage.set(STORAGE_KEYS.RIDE_DATE, flightDateOnly.toString());
                            storage.set(STORAGE_KEYS.RIDE_TIME, flightDateObj.toString());
                            storage.get(STORAGE_KEYS.EDIT_STEP) === true ? props.setStep('orderDetails') : props.setStep('passengerQuantity');
                        } else if (forcedFlightId) {
                            storage.set(STORAGE_KEYS.FLIGHT_NUMBER, flightNumber);
                            storage.get(STORAGE_KEYS.EDIT_STEP) === true ? props.setStep('orderDetails') : props.setStep('passengerQuantity');
                        }
                    }}
                    disabled={!(flightNumberDetails.length > 0) && !forcedFlightId}
                >
                    {storage.get(STORAGE_KEYS.EDIT_STEP) === true ? "Done" : "Continue"}
                </button>
            </ModalFooter>
        </>
    );
}
export default FlightNumber;
